import type { TechTest, TestEvaluation } from '../types';

/**
 * Calcule le statut global d'un test à partir de l'état de ses évaluations.
 * Logique reprise 1:1 du legacy.
 */
export function calculateTestStatus(test: TechTest): TechTest['status'] {
  const now = Date.now();
  if (test.deadline && now > test.deadline) return 'expired';

  const hasStartedEvaluation = test.evaluations.some(
    (ev) => ev.status === 'in-progress' || ev.status === 'completed',
  );
  const allCompleted = test.evaluations.every((ev) => ev.status === 'completed');

  if (allCompleted) return 'completed';
  if (hasStartedEvaluation) return 'in-progress';
  return 'not-started';
}

/** ID court unique préfixé. */
export function uid(prefix: string): string {
  return `${prefix}-${String(Date.now())}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Vrai quand l'évaluation a atteint un état terminal et n'accepte plus aucune
 * action : `completed` (travail soumis), `failed` (attempt abandonné) ou
 * `evaluation-failed` (ms-eval a renoncé à l'évaluer, SAAS-1018).
 *
 * @remarks
 * Dans les trois cas l'évaluation reste **consultable** — ressources, documents
 * de phase, espace de travail et récap de soumission restent lisibles — mais
 * tout ce qui écrit est verrouillé : soumission, abandon, édition des documents
 * de travail, annotations, chat personas (SAAS-951).
 *
 * Le verrou dérive du STATUT, jamais de la seule présence d'une `submission` :
 * `buildSubmission()` renvoie `undefined` quand la projection back n'inclut pas
 * les `submittedResources` (cas de `findByTestKey` côté ms-graph), ce qui
 * laisserait le formulaire éditable après un simple rechargement de page. Un
 * attempt abandonné n'a de toute façon jamais de `submission`.
 */
export function isReadOnlyEvaluation(
  evaluation: Pick<TestEvaluation, 'status'> | null | undefined,
): boolean {
  return (
    evaluation?.status === 'completed' ||
    evaluation?.status === 'failed' ||
    evaluation?.status === 'evaluation-failed'
  );
}

/**
 * Clé d'attempt à utiliser pour **lire** l'historique rattaché à l'évaluation
 * (bookmarks, annotations).
 *
 * @remarks
 * L'attempt en cours prime ; à défaut on retombe sur le dernier attempt terminé
 * (soumis ou abandonné), ce qui rend cet historique consultable après coup —
 * `activeAttemptKey` n'étant plus posé une fois l'attempt clos (SAAS-951).
 *
 * ⚠️ Réservé à la lecture : une évaluation terminée n'accepte plus aucune
 * mutation (cf. `isReadOnlyEvaluation`).
 */
export function resolveReadAttemptKey(
  evaluation: Pick<TestEvaluation, 'activeAttemptKey' | 'lastAttemptKey'> | null | undefined,
): string | undefined {
  return evaluation?.activeAttemptKey ?? evaluation?.lastAttemptKey;
}
